import { useEffect, useMemo } from "react";
import Tooltip from "@src/components/Tooltip";
import { UIButton } from "@src/components/UIButton";
import UIModal from "@src/components/UIModal/UIModal";
import { FEATURES } from "@src/constants";
import { usePlaylistTagsQuery } from "@src/queries/playlistTagsQueries";
import { theme } from "@src/theme";
import styled from "styled-components";
import Spinner from "@src/components/Spinner";
import { IPlaylistRecord } from "@src/types/playlist";
import CategoryIcon from "./CategoryIcon";
import PlaylistSubtagsPicker, {
  usePlaylistTagSelection,
} from "./PlaylistSubtagsPicker";

type UpdatePlaylistTagsProps = {
  onConfirm: (
    playlistTagsSelected: string[],
    /** `undefined` while `FEATURES.PLAYLIST_SUBTAGS_ENABLED` is off. */
    playlistSubtagsSelected: string[] | undefined
  ) => void;
  onClose: () => void;
  isOpen: boolean;
  loading?: boolean;
  playlistData: IPlaylistRecord | null;
};

const sameIds = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((id) => b.includes(id));

const UpdatePlaylistTags: React.FC<UpdatePlaylistTagsProps> = ({
  onConfirm,
  onClose,
  isOpen,
  loading,
  playlistData,
}) => {
  const { data: categories, isFetching } = usePlaylistTagsQuery({
    enabled: isOpen,
    withSubtags: true,
  });
  const selection = usePlaylistTagSelection(categories);
  const {
    selectedCategories,
    selectedSubtagIds,
    onSelectCategory,
    reset: resetSelection,
  } = selection;

  /*
   * Keyed by the ids rather than the arrays, so a refetch of the same playlist
   * does not wipe what is being picked.
   */
  const currentCategoryKey = (playlistData?.playlistTags ?? [])
    .filter((tag) => tag != null)
    .map((tag) => tag.documentId)
    .join(",");
  const currentSubtagKey = (playlistData?.subPlaylistTags ?? [])
    .filter((subtag) => subtag != null)
    .map((subtag) => subtag.documentId)
    .join(",");
  const currentCategoryIds = useMemo(
    () => (currentCategoryKey ? currentCategoryKey.split(",") : []),
    [currentCategoryKey]
  );
  const currentSubtagIds = useMemo(
    () => (currentSubtagKey ? currentSubtagKey.split(",") : []),
    [currentSubtagKey]
  );

  /**
   * Every visit starts from what the playlist has saved, so leaving by the
   * overlay or the X drops whatever was picked. Re-runs once the tag list has
   * loaded, which is what places each saved subtag under its tag [ROVR-3982].
   */
  useEffect(() => {
    if (!isOpen) return;
    resetSelection(currentCategoryIds, currentSubtagIds);
  }, [isOpen, resetSelection, currentCategoryIds, currentSubtagIds]);

  const hasChanges =
    !sameIds(currentCategoryIds, selectedCategories) ||
    (FEATURES.PLAYLIST_SUBTAGS_ENABLED &&
      !sameIds(currentSubtagIds, selectedSubtagIds));

  return (
    <UIModal
      modalStyles={{
        // Same width as the submit popup, so the subtag pills wrap the same way.
        width: 538,
        maxWidth: "calc(100vw - 32px)",
      }}
      onClose={onClose}
      isOpen={isOpen}
      actions={[
        <UIButton key="cancel" onClick={onClose} variant="secondaryOutline">
          Cancel
        </UIButton>,
        <UIButton
          key="delete"
          onClick={() =>
            onConfirm(
              selectedCategories,
              FEATURES.PLAYLIST_SUBTAGS_ENABLED ? selectedSubtagIds : undefined
            )
          }
          variant="primary"
          disabled={
            !selectedCategories.length || isFetching || loading || !hasChanges
          }
        >
          Submit
        </UIButton>,
      ]}
      headerStyles={{
        textAlign: "center",
      }}
      title="Update playlist tags."
    >
      <p
        style={{
          width: "100%",
          textAlign: "center",
          color: theme.colors.gray,
        }}
      >
        Select at least one category that best describes the vibe of your
        playlist
      </p>

      <CategoriesContainer>
        {categories?.map((category) => (
          <Tooltip
            content={category.description || ""}
            key={category.documentId}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                userSelect: "none",
              }}
            >
              <CategoryButton
                aria-selected={selectedCategories.includes(category.documentId)}
                onClick={() => onSelectCategory(category.documentId)}
                $active={selectedCategories.includes(category.documentId)}
                className={
                  selectedCategories.includes(category.documentId)
                    ? "category-active"
                    : ""
                }
              >
                <CategoryIcon
                  url={category.icon?.url}
                  alt={
                    category.icon?.alternativeText ||
                    category.label ||
                    "Category icon"
                  }
                />
              </CategoryButton>
              <span
                style={{
                  fontSize: "12px",
                  textTransform: "uppercase",
                  textAlign: "center",
                  color: theme.colors.white,
                }}
              >
                {category.label || "Unnamed Tag"}
              </span>
            </div>
          </Tooltip>
        ))}
        {(isFetching || loading) && <Spinner small />}
      </CategoriesContainer>

      <PlaylistSubtagsPicker
        categoriesWithSubtags={selection.categoriesWithSubtags}
        expandedCategoryId={selection.expandedCategoryId}
        selectedSubtags={selection.selectedSubtags}
        onSelectSubtag={selection.onSelectSubtag}
        onExpandCategory={selection.onExpandCategory}
      />
    </UIModal>
  );
};

const CategoriesContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  min-height: 50px;
  padding: 10px;
`;
const CategoryButton = styled.button<{ $active: boolean }>`
  user-drag: none;
  -webkit-user-drag: none;
  -moz-user-select: none;
  margin-bottom: 8px;
  cursor: pointer;
  background: ${(props): string =>
    props.$active ? theme.colors.primary : "transparent"};
  height: 44px;
  width: 44px;
  object-fit: contain;
  border: none;
  border-radius: 50%;
  user-select: none;
  /*
   * The icon is a mask painted with this colour (see CategoryIcon), so every
   * tag reads the same whatever colour its asset was uploaded in.
   */
  color: ${(props): string =>
    props.$active ? theme.colors.black : theme.colors.primary};
  transition:
    background 0.2s ease-in-out,
    color 0.2s ease-in-out,
    border 0.2s ease-in-out;
`;

export default UpdatePlaylistTags;
